import { DateRange } from 'owa-datetime-utils';
import { getAgendaStore, AgendaScenario, AgendaScenarioViewConfig } from '../store/agendaStore';
import { addDays, OwaDate } from 'owa-datetime';
import { getNumberOfDaysToLoad } from '../utils/getNumberOfDaysToLoad';
import { createLockedCalendarEventsStore, EventsCacheLockId } from 'owa-calendar-events-store';

/** TODO VSO 47572: when an all Day event is selected, it selects all events in the list, because all the items share a clientItemID. */
export function isSelectedCalendarEvent(id: string, scenarioId: string): boolean {
    return getScenario(scenarioId).selectedCalendarEventId === id;
}

/**
 * checks if the eventsCacheLockId was created via `getScenarioUpNextCacheId`
 * @param eventsCacheLockId eventsCacheLockId
 * @returns { value: true, scenarioId: string } if eventsCacheLockId was created by `getScenarioUpNextCacheId`, where
 * scenarioId is the scenario for which `getScenarioUpNextCacheId` was called.
 * @returns { value: false } if eventsCacheLockId was not created via `getScenarioUpNextCacheId`
 */
export function isUpNextCalendarCacheId(
    eventsCacheLockId: EventsCacheLockId
): { value: false } | { value: true; scenarioId: string } {
    const scenarios = [...getAgendaStore().scenarios.entries()].filter(
        ([key, agendaScenario]) => getScenarioUpNextCacheId(key) === eventsCacheLockId
    );
    return scenarios.length > 0 ? { value: true, scenarioId: scenarios[0][0] } : { value: false };
}

/**
 * checks if the eventsCacheLockId was created via `getScenarioListCacheId`
 * @param eventsCacheLockId eventsCacheLockId
 * @returns { value: true, scenarioId: string } if eventsCacheLockId was created by `getScenarioListCacheId`, where
 * scenarioId is the scenario for which `getScenarioListCacheId` was called.
 * @returns { value: false } if eventsCacheLockId was not created via `getScenarioListCacheId`
 */
export function isListCalendarCacheId(
    eventsCacheLockId: EventsCacheLockId
): { value: false } | { value: true; scenarioId: string } {
    const scenarios = [...getAgendaStore().scenarios.entries()].filter(
        ([key, agendaScenario]) => getScenarioListCacheId(key) === eventsCacheLockId
    );
    return scenarios.length > 0 ? { value: true, scenarioId: scenarios[0][0] } : { value: false };
}

/**
 * Checks whether or not to show declined events for the scenario
 * @param scenarioId
 * @returns true if the scenario should show declined events
 */
export function shouldShowDeclinedEvents(scenarioId: string): boolean {
    return getScenario(scenarioId).shouldShowDeclinedEvents;
}

export function getScenario(scenarioId: string): AgendaScenario {
    const scenario = getAgendaStore().scenarios.get(scenarioId);
    if (!scenario) {
        throw new Error('getScenario: agenda scenario is not initialized ' + scenarioId);
    }
    return scenario;
}

export function isScenarioInitialized(scenarioId: string): boolean {
    return getAgendaStore().scenarios.has(scenarioId);
}

export function getScenarioUpNextCacheId(scenarioId: string): EventsCacheLockId {
    const lockedCalendarEventsStore = createLockedCalendarEventsStore(scenarioId + '_AgendaUpNext');
    return lockedCalendarEventsStore.getLockId();
}

export function getScenarioListCacheId(scenarioId: string): EventsCacheLockId {
    const lockedCalendarEventsStore = createLockedCalendarEventsStore(scenarioId + '_AgendaList');
    return lockedCalendarEventsStore.getLockId();
}

export function getTopOfListIndex(scenarioId: string): number {
    return getScenario(scenarioId).topOfListIndex;
}

export function getShouldAutoScroll(scenarioId: string): boolean {
    return getScenario(scenarioId).shouldAutoScroll;
}

export function getViewConfig(scenarioId: string): AgendaScenarioViewConfig {
    return getScenario(scenarioId).viewConfig;
}

export function shouldShowTasks(scenarioId: string): boolean {
    return getScenario(scenarioId).shouldShowTasks;
}

export function getSearchText(scenarioId: string): string | null {
    return getScenario(scenarioId).searchText;
}

export function getInitialDateRange(scenarioId: string, date: OwaDate): DateRange {
    const maxDays = getViewConfig(scenarioId).maxDays;
    const daysToLoad = getNumberOfDaysToLoad(null, maxDays);
    const initialDateRangeToLoad = {
        start: date,
        end: addDays(date, daysToLoad),
    };
    return initialDateRangeToLoad;
}
